import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add, Edit, AccountBalance, CreditCard, Description } from '@mui/icons-material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { banksAPI, Bank } from '../services/api';
import BankStatementsPage from './BankStatementsPage';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`banks-tabpanel-${index}`}
      aria-labelledby={`banks-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const BanksPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState<Bank | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    credit_card: false,
    initial_amount: '',
    pdf_password: '',
    netbank_username: '',
    netbank_password: '',
  });

  const formatCurrency = (value: any): string => {
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(num)) return '₹0.00';
    
    // Use JavaScript's built-in Indian number formatting
    return `₹${num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found, user not authenticated');
      setError('User not authenticated');
      return;
    }
    
    console.log('User authenticated, fetching banks...');
    fetchBanks();
  }, []);

  // Safety check: ensure banks is always an array
  useEffect(() => {
    if (!Array.isArray(banks)) {
      console.warn('Banks is not an array, resetting to empty array');
      setBanks([]);
    }
  }, [banks]);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      console.log('Fetching banks...');
      console.log('Token:', localStorage.getItem('token'));
      
      const response = await banksAPI.getAll();
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      console.log('Response status:', response.status);
      
      // Handle new API response format for banks
      const banksData = response.data.data || response.data || [];
      console.log('Banks data extracted:', banksData);
      console.log('Banks data type:', typeof banksData);
      console.log('Is array:', Array.isArray(banksData));
      
      // Ensure we always set an array
      if (Array.isArray(banksData)) {
        console.log('Setting banks array:', banksData);
        setBanks(banksData);
      } else if (banksData && typeof banksData === 'object') {
        // If it's an object, try to extract the data
        console.warn('Banks data is an object, attempting to extract array:', banksData);
        if (Array.isArray(banksData.data)) {
          console.log('Extracted banks array from object:', banksData.data);
          setBanks(banksData.data);
        } else {
          console.error('Could not extract banks array from object:', banksData);
          setBanks([]);
        }
      } else {
        console.error('Banks data is not an array:', banksData);
        setBanks([]);
      }
    } catch (error: any) {
      console.error('Error fetching banks:', error);
      console.error('Error response:', error.response);
      setError(error.response?.data?.error || 'Failed to fetch banks');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bank: Bank) => {
    setEditingBank(bank);
    setFormData({
      name: bank.name,
      code: bank.code,
      credit_card: Boolean(bank.credit_card),
      initial_amount: (bank.initial_amount || 0).toString(),
      pdf_password: bank.pdf_password || '',
      netbank_username: bank.netbank_username || '',
      netbank_password: bank.netbank_password || '',
    });
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingBank(null);
    setFormData({
      name: '',
      code: '',
      credit_card: false,
      initial_amount: '0',
      pdf_password: '',
      netbank_username: '',
      netbank_password: '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        credit_card: formData.credit_card,
        initial_amount: parseFloat(formData.initial_amount || '0'),
        pdf_password: formData.pdf_password || undefined,
        netbank_username: formData.netbank_username || undefined,
        netbank_password: formData.netbank_password || undefined,
      };

      if (!payload.name || !payload.code) {
        setError('Bank name and code are required');
        return;
      }
      
      if (editingBank) {
        await banksAPI.update(editingBank.id, payload);
      } else {
        await banksAPI.create(payload);
      }

      setDialogOpen(false);
      setEditingBank(null);
      fetchBanks();
    } catch (error: any) {
      setError(error.response?.data?.errors?.join(', ') || `Failed to ${editingBank ? 'update' : 'create'} bank`);
    }
  };

  if (loading && banks.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Banks</Typography>
        {activeTab === 0 && (
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            New Bank
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Banks" icon={<AccountBalance />} iconPosition="start" />
          <Tab label="Bank Statements" icon={<Description />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={activeTab} index={0}>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Manage Initial Balances
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Set the initial balance for each bank. This amount will be added to all balance calculations.
          </Typography>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bank Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell align="right">Initial Amount</TableCell>
              <TableCell>Netbank Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(banks) && banks.map((bank) => (
              <TableRow key={bank.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {bank.credit_card ? (
                      <CreditCard sx={{ mr: 1, color: 'secondary.main' }} />
                    ) : (
                      <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                    )}
                    {bank.name}
                    {bank.credit_card && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          ml: 1, 
                          px: 1, 
                          py: 0.5, 
                          bgcolor: 'secondary.main', 
                          color: 'white', 
                          borderRadius: 1,
                          fontSize: '0.7rem'
                        }}
                      >
                        Credit Card
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{bank.code}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(bank.initial_amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {bank.netbank_configured ? (
                    <Chip 
                      label="Configured" 
                      color="success" 
                      size="small" 
                      variant="outlined"
                    />
                  ) : (
                    <Chip 
                      label="Not Configured" 
                      color="default" 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEdit(bank)}>
                    <Edit />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!Array.isArray(banks) || banks.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {loading ? 'Loading banks...' : 'No banks available'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBank ? `Edit Bank - ${editingBank.name}` : 'New Bank'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Bank Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2 }}
            helperText="Display name for this bank"
          />

          <TextField
            fullWidth
            label="Bank Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            sx={{ mt: 2 }}
            helperText="Unique short code, e.g. HDFC, CASH, HDFCCC"
          />

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={formData.credit_card}
                onChange={(e) => setFormData({ ...formData, credit_card: e.target.checked })}
              />
            }
            label="Credit card"
          />

          <TextField
            fullWidth
            label="Initial Amount"
            type="number"
            value={formData.initial_amount}
            onChange={(e) => setFormData({ ...formData, initial_amount: e.target.value })}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
            }}
            helperText="Enter the initial balance for this bank"
          />
          
          <TextField
            fullWidth
            label="PDF Password"
            type="password"
            value={formData.pdf_password}
            onChange={(e) => setFormData({ ...formData, pdf_password: e.target.value })}
            sx={{ mt: 2 }}
            helperText="Password for PDF bank statements (optional)"
          />
          
          <TextField
            fullWidth
            label="Netbank Username"
            value={formData.netbank_username}
            onChange={(e) => setFormData({ ...formData, netbank_username: e.target.value })}
            sx={{ mt: 2 }}
            helperText="Username for netbanking access (optional)"
          />
          
          <TextField
            fullWidth
            label="Netbank Password"
            type="password"
            value={formData.netbank_password}
            onChange={(e) => setFormData({ ...formData, netbank_password: e.target.value })}
            sx={{ mt: 2 }}
            helperText="Password for netbanking access (optional)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <BankStatementsPage />
      </TabPanel>
    </Box>
  );
};

export default BanksPage;
